import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginThunk } from '../store/slices/usuario.slice';
import { 
  User, Lock, Eye, EyeOff, Loader, ChevronRight, 
  GraduationCap, Globe, ShieldCheck, 
  TrendingUp, Target, MessageSquare, BookOpen
} from 'lucide-react';

export const Login = () => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const createParticle = () => {
      const particle = document.createElement('div');
      particle.className = 'absolute rounded-full pointer-events-none';
      
      const size = Math.random() * 20 + 5;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.left = `${Math.random() * 100}%`;
      particle.style.top = `${Math.random() * 100}%`;
      
      const colors = [
        'bg-blue-400/20', 'bg-purple-400/20', 'bg-theme-blue/20', 
        'bg-theme-yellow/20', 'bg-indigo-400/20'
      ];
      particle.classList.add(colors[Math.floor(Math.random() * colors.length)]);
      
      particle.style.animation = `float ${Math.random() * 6 + 4}s ease-in-out infinite`;
      particle.style.animationDelay = `${Math.random() * 2}s`;
      
      document.querySelector('.particles-container')?.appendChild(particle);
      
      setTimeout(() => particle.remove(), 12000);
    };
    
    const interval = setInterval(createParticle, 600);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data = Object.fromEntries(new FormData(e.target));
      await dispatch(loginThunk(data, navigate));
    } catch (err) {
      setError(err.response?.data?.message || 'Error inesperado');
    } finally {
      setIsLoading(false);
    }
  };  

  return (
    <div className="relative min-h-screen flex flex-col justify-between bg-gradient-to-br from-blue-50 via-white to-yellow-50 overflow-hidden">
      <div className="particles-container absolute inset-0 overflow-hidden z-0"></div>
      
      <div 
        className="fixed w-6 h-6 bg-theme-blue/30 rounded-full pointer-events-none z-50 transition-all duration-300 ease-out mix-blend-multiply"
        style={{
          left: mousePosition.x - 12,
          top: mousePosition.y - 12,
          transform: isHovered ? 'scale(2)' : 'scale(1)',
          opacity: isHovered ? 0.8 : 0.5
        }}
      />
      
      <div className="absolute top-[-150px] left-[-150px] w-[600px] h-[600px] bg-theme-blue/15 rounded-full blur-[130px] animate-pulse-slow z-0" />
      <div className="absolute bottom-[-180px] right-[-180px] w-[700px] h-[700px] bg-theme-yellow/15 rounded-full blur-[140px] animate-pulse-slow z-0" />
      <div className="absolute top-1/4 left-1/5 w-[400px] h-[400px] bg-blue-300/10 rounded-full blur-[120px] animate-pulse z-0" />
      
      <main className="z-10 relative flex flex-col justify-center items-center flex-grow px-4 py-8">
        <div className="w-full max-w-6xl bg-white/95 backdrop-blur-xl rounded-[2rem] shadow-[0_30px_70px_-15px_rgba(0,0,0,0.2)] p-8 sm:p-12 border border-white/60 flex flex-col lg:flex-row gap-10 animate-fade-in">
          
          <div className="flex-1 flex flex-col justify-center p-8 bg-gradient-to-br from-theme-blue/5 to-theme-yellow/5 rounded-2xl relative overflow-hidden">
            
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-theme-blue/15 blur-3xl z-0" />
            <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-theme-yellow/15 blur-3xl z-0" />
            
            <div className="flex justify-center mb-8 relative z-10">
              <div className="relative bg-white p-6 rounded-3xl shadow-xl border border-gray-100 transform transition-all duration-300 hover:scale-105 group">
                <div className="absolute inset-0 bg-gradient-to-r from-theme-blue/20 to-theme-yellow/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative z-10 flex justify-center">
                  <img
                    src="/img/iconos/Eduserver-LOGO-v2.svg"
                    alt="EduServer Logo"
                    className="h-52 sm:h-60 md:h-64 transition-transform duration-300 hover:scale-105"
                  />
                </div>
              </div>
            </div>
            
            <div className="text-center mb-10 relative z-10">
              <h2 className="text-4xl sm:text-5xl font-black text-slate-800 mb-4 leading-tight">
                Plataforma Educativa
              </h2>
              <p className="text-xl text-gray-600 max-w-lg mx-auto font-medium">
                Transformando el futuro con tecnología
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto relative z-10">
              <div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm p-4 rounded-xl border border-white/60 hover:shadow-lg transition-all duration-300 group hover:-translate-y-1">
                <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-3 rounded-full group-hover:scale-110 transition-transform">
                  <BookOpen className="text-theme-blue w-6 h-6" />
                </div>
                <div>
                  <div className="text-gray-800 font-bold text-sm">Educación Premium</div>
                  <div className="text-gray-600 text-xs">Contenido de calidad</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm p-4 rounded-xl border border-white/60 hover:shadow-lg transition-all duration-300 group hover:-translate-y-1">
                <div className="bg-gradient-to-br from-green-100 to-green-200 p-3 rounded-full group-hover:scale-110 transition-transform">
                  <ShieldCheck className="text-green-600 w-6 h-6" />
                </div>
                <div>
                  <div className="text-gray-800 font-bold text-sm">Seguridad Total</div>
                  <div className="text-gray-600 text-xs">Protección garantizada</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm p-4 rounded-xl border border-white/60 hover:shadow-lg transition-all duration-300 group hover:-translate-y-1">
                <div className="bg-gradient-to-br from-purple-100 to-purple-200 p-3 rounded-full group-hover:scale-110 transition-transform">
                  <TrendingUp className="text-purple-600 w-6 h-6" />
                </div>
                <div>
                  <div className="text-gray-800 font-bold text-sm">Progreso Medible</div>
                  <div className="text-gray-600 text-xs">Seguimiento continuo</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm p-4 rounded-xl border border-white/60 hover:shadow-lg transition-all duration-300 group hover:-translate-y-1">
                <div className="bg-gradient-to-br from-theme-yellow/20 to-theme-yellow/30 p-3 rounded-full group-hover:scale-110 transition-transform">
                  <Target className="text-theme-yellow w-6 h-6" />
                </div>
                <div>
                  <div className="text-gray-800 font-bold text-sm">Objetivos Claros</div>
                  <div className="text-gray-600 text-xs">Metas personalizadas</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex-1 flex flex-col justify-center max-w-md mx-auto lg:mx-0">
            <div className="mb-8 text-center">
              <h3 className="text-4xl font-black text-slate-800 mb-3">Inicia Sesión</h3>
              <p className="text-gray-600 text-lg font-medium">Accede a tu mundo de aprendizaje</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-gray-500 group-focus-within:text-theme-blue transition-all duration-300">
                  <User size={22} />
                </div>
                <input
                  name="username"
                  placeholder="Nombre de usuario"
                  className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-gray-200 focus:ring-4 focus:ring-theme-blue/20 focus:border-theme-blue outline-none bg-white/95 backdrop-blur-sm transition-all duration-300 hover:border-theme-blue/50 text-lg font-medium placeholder-gray-400"
                  required
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                />
              </div>
              
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none text-gray-500 group-focus-within:text-theme-blue transition-all duration-300">
                  <Lock size={22} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Contraseña"
                  className="w-full pl-12 pr-14 py-4 rounded-2xl border-2 border-gray-200 focus:ring-4 focus:ring-theme-blue/20 focus:border-theme-blue outline-none bg-white/95 backdrop-blur-sm transition-all duration-300 hover:border-theme-blue/50 text-lg font-medium placeholder-gray-400"
                  required
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-theme-blue transition-colors duration-300"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                </button>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center group">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-5 w-5 text-theme-blue rounded border-gray-300 focus:ring-theme-blue focus:ring-2 transition-all"
                  />
                  <label htmlFor="remember-me" className="ml-3 block text-sm text-gray-700 font-medium group-hover:text-theme-blue transition-colors cursor-pointer">
                    Mantener sesión iniciada
                  </label>
                </div>
                <a href="#" className="text-sm text-theme-blue hover:text-blue-800 font-bold hover:underline transition-colors">
                  ¿Olvidaste tu contraseña?
                </a>
              </div>
              
              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 animate-shake">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <p className="text-red-700 text-sm font-bold">{error}</p>
                  </div>
                </div>
              )}
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full group relative bg-gradient-to-r from-theme-yellow to-theme-yellow hover:from-theme-yellow/90 hover:to-theme-blue/70 text-white font-bold py-4 px-6 rounded-2xl flex items-center justify-center transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-theme-blue/30 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none overflow-hidden"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative z-10 flex items-center text-lg">
                  {isLoading ? (
                    <>
                      <Loader className="animate-spin mr-3" size={24} />
                      Verificando credenciales...
                    </>
                  ) : (
                    <>
                      <span>Acceder</span>
                      <ChevronRight className="ml-3 group-hover:translate-x-1 transition-transform duration-300" size={24} />
                    </>
                  )}
                </span>
              </button>
              
              <div className="text-center pt-6 border-t border-gray-200">
                <p className="text-gray-600 text-base font-medium">
                  ¿Primera vez?{' '}
                  <a href="#" className="text-theme-blue font-bold hover:text-blue-800 hover:underline transition-colors">
                    Solicita tu acceso aquí
                  </a>
                </p>
              </div>
            </form>
          </div>
        </div>
      </main>

      <footer className="w-full bg-gradient-to-r from-theme-blue/5 to-theme-yellow/5 backdrop-blur-xl py-8 relative z-10 border-t border-white/60">
        <div className="text-center text-sm text-gray-600 space-y-3 px-4 max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-theme-blue to-theme-yellow rounded-full flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-white" />
              </div>
              <p className="font-bold text-theme-blue text-base">Instituto Educativo</p>
            </div>
            <div className="hidden sm:block w-px h-6 bg-gray-300"></div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-500" />
              <p className="font-medium">Santa Cruz, Bolivia</p>
            </div>
            <div className="hidden sm:block w-px h-6 bg-gray-300"></div>
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gray-500" />
              <p className="font-medium">eduserver.bo@gmail.com</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-theme-blue/10 to-theme-yellow/10 rounded-2xl p-4 mb-4">
            <p className="italic text-gray-700 font-bold text-lg">"Somos Familia, somos Educación"</p>
            <p className="text-gray-600 text-sm mt-1">Conectando mentes, construyendo futuro</p>
          </div>
          
          <p>
            <a
              href="https://www.google.com/maps/place/17%C2%B048'52.2%22S+63%C2%B011'14.3%22W"
              target="_blank"
              rel="noopener noreferrer"
              className="text-theme-blue font-bold hover:text-blue-800 hover:underline inline-flex items-center gap-2 transition-colors bg-theme-blue/10 px-4 py-2 rounded-xl"
            >
              <span>📍 Ver nuestra ubicación</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};